// src/pages/Admin/Content/ContentManagement.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  Clock,
  Star,
  Download,
  Bell
} from 'lucide-react';
import adminService, { type ContentItem } from '../../../services/adminService';
import { useToast } from '../../../hooks/useToast';
import ConfirmDialog from '../../../components/Admin/ConfirmDialog';
import FullContentFormModal from '../../../components/Admin/FullContentFormModal';

interface ContentFilters {
  content_type: string;
  status: string;
  search: string;
  page: number;
  page_size: number;
}

const ContentManagement: React.FC = () => {
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<ContentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedContent, setSelectedContent] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void;
    danger?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: () => {},
  });

  const [contentFormModal, setContentFormModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit' | 'view';
    contentItem?: ContentItem;
  }>({
    isOpen: false,
    mode: 'add',
  });

  // Extract content type from URL path
  const getContentTypeFromPath = () => {
    const path = location.pathname;
    if (path.includes('/cinema')) return 'CINEMA';
    if (path.includes('/contents')) return 'CONTENT';
    if (path.includes('/podcasts')) return 'PODCAST';
    if (path.includes('/animations')) return 'ANIMATION';
    if (path.includes('/sneak-peeks')) return 'SNEAK_PEEK';
    if (path.includes('/live-videos')) return 'LIVE_VIDEO';
    return '';
  };

  const [filters, setFilters] = useState<ContentFilters>({
    content_type: getContentTypeFromPath(),
    status: '',
    search: '',
    page: 1,
    page_size: 20
  });

  // Update filters when URL changes
  useEffect(() => {
    const path = location.pathname;
    let contentType = '';
    if (path.includes('/cinema')) contentType = 'CINEMA';
    else if (path.includes('/contents')) contentType = 'CONTENT';
    else if (path.includes('/podcasts')) contentType = 'PODCAST';
    else if (path.includes('/animations')) contentType = 'ANIMATION';
    else if (path.includes('/sneak-peeks')) contentType = 'SNEAK_PEEK';
    else if (path.includes('/live-videos')) contentType = 'LIVE_VIDEO';
    
    setFilters(prev => ({
      ...prev,
      content_type: contentType,
      page: 1
    }));
  }, [location.pathname]);

  const contentTypes = [
    { value: '', label: 'All Types' },
    { value: 'CINEMA', label: 'Cinema' },
    { value: 'CONTENT', label: 'Contents' },
    { value: 'PODCAST', label: 'Podcasts' },
    { value: 'ANIMATION', label: 'Animations' },
    { value: 'SNEAK_PEEK', label: 'Sneak Peeks' },
    { value: 'LIVE_VIDEO', label: 'Live Videos' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'PUBLISHED', label: 'Published' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ARCHIVED', label: 'Archived' },
    { value: 'SCHEDULED', label: 'Scheduled' }
  ];

  const loadContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getContentManagement(filters);
      setContent(response.content);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Error loading content:', error);
      setContent([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleFilterChange = (key: keyof ContentFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value as number : 1 // Reset to page 1 for other filters
    }));
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value,
      page: 1
    }));
  };

  const handleSelectContent = (contentId: string) => {
    setSelectedContent(prev => 
      prev.includes(contentId) 
        ? prev.filter(id => id !== contentId)
        : [...prev, contentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContent.length === content.length) {
      setSelectedContent([]);
    } else {
      setSelectedContent(content.map(item => item.id));
    }
  };

  const handleDeleteContent = (contentType: string, contentId: string, title: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Content',
      message: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
      action: async () => {
        try {
          await adminService.deleteContent(contentType, contentId);
          showSuccess('Content deleted successfully');
          loadContent();
          setSelectedContent(prev => prev.filter(id => id !== contentId));
        } catch (_error) {
          showError('Failed to delete content');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      danger: true
    });
  };

  const handleBulkDelete = () => {
    if (selectedContent.length === 0) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Multiple Content',
      message: `Are you sure you want to delete ${selectedContent.length} selected content items? This action cannot be undone.`,
      action: async () => {
        try {
          const contentItems = content
            .filter(item => selectedContent.includes(item.id))
            .map(item => ({ content_type: item.contentType, content_id: item.id }));
          
          await adminService.bulkDeleteContent(contentItems);
          showSuccess(`Successfully deleted ${selectedContent.length} content items`);
          loadContent();
          setSelectedContent([]);
        } catch (_error) {
          showError('Failed to delete content');
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
      danger: true
    });
  };

  const handleAddContent = () => {
    setContentFormModal({
      isOpen: true,
      mode: 'add',
    });
  };

  const handleEditContent = (contentItem: ContentItem) => {
    setContentFormModal({
      isOpen: true,
      mode: 'edit',
      contentItem,
    });
  };

  const handleViewContent = (contentItem: ContentItem) => {
    setContentFormModal({
      isOpen: true,
      mode: 'view',
      contentItem,
    });
  };

  const handleCloseContentModal = () => {
    setContentFormModal({
      isOpen: false,
      mode: 'add',
    });
  };

  const [notifyingContentId, setNotifyingContentId] = useState<string | null>(null);

  const handleNotifyUsers = (contentItem: ContentItem) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Notify Users',
      message: `Send email notifications to all users and newsletter subscribers about "${contentItem.title}"?`,
      action: async () => {
        setNotifyingContentId(contentItem.id);
        try {
          await adminService.notifyContentUsers(contentItem.id);
          showSuccess(`Notification emails are being sent for: "${contentItem.title}"`);
        } catch (_error) {
          showError('Failed to send notifications');
        } finally {
          setNotifyingContentId(null);
        }
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleContentSave = async (formData: FormData) => {
    try {
      if (contentFormModal.mode === 'add') {
        // Create new content
        const title = formData.get('title') as string;
        const contentType = formData.get('contentType') as string;
        
        await adminService.createContent(formData);
        showSuccess(`Successfully created ${contentType.toLowerCase()}: "${title}"`);
      } else if (contentFormModal.mode === 'edit' && contentFormModal.contentItem) {
        // Update existing content
        const title = formData.get('title') as string;
        const contentId = contentFormModal.contentItem.id;
        
        await adminService.updateContent(contentId, formData);
        showSuccess(`Successfully updated: "${title}"`);
      }
      handleCloseContentModal();
      loadContent();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save content';
      showError(errorMessage);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800'
    };

    const icons = {
      published: CheckCircle,
      draft: Clock,
      pending: Clock,
      archived: XCircle
    };

    const Icon = icons[status as keyof typeof icons] || Clock;
    const className = styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getContentTypeBadge = (type: string) => {
    const colors = {
      CINEMA: 'bg-orange-100 text-orange-800',
      CONTENT: 'bg-blue-100 text-blue-800', 
      PODCAST: 'bg-green-100 text-green-800',
      ANIMATION: 'bg-orange-100 text-orange-800',
      SNEAK_PEEK: 'bg-pink-100 text-pink-800',
      LIVE_VIDEO: 'bg-red-100 text-red-800'
    };

    const className = colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        {type.replace('_', ' ')}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / filters.page_size);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Content Management</h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600">
              Manage all content across your platform ({totalCount} total items)
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button 
              onClick={() => showSuccess('Export functionality coming soon!')}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button 
              onClick={handleAddContent}
              className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Content
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
          {/* Search */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search content..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content Type Filter */}
          <div>
            <select
              value={filters.content_type}
              onChange={(e) => handleFilterChange('content_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {contentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Page Size */}
          <div>
            <select
              value={filters.page_size}
              onChange={(e) => handleFilterChange('page_size', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {selectedContent.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete ({selectedContent.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : content.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No content found matching your criteria</p>
          </div>
        ) : (
          <div className="table-container scrollbar-thin">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8 sm:w-12">
                    <input
                      type="checkbox"
                      checked={selectedContent.length === content.length && content.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Content
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Type
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                    Engagement
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Created
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {content.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedContent.includes(item.id) ? 'bg-orange-50' : ''
                    }`}
                  >
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedContent.includes(item.id)}
                        onChange={() => handleSelectContent(item.id)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            by {item.author.username}
                          </div>
                          {/* Show inline badges on mobile */}
                          <div className="mt-1 flex flex-wrap gap-1 sm:hidden">
                            {getContentTypeBadge(item.contentType)}
                            {getStatusBadge(item.status)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                      {getContentTypeBadge(item.contentType)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                      <div className="flex items-center space-x-2 lg:space-x-4">
                        <div className="flex items-center">
                          <Eye className="h-4 w-4 mr-1" />
                          <span className="hidden lg:inline">{item.viewCount?.toLocaleString() || '0'}</span>
                          <span className="lg:hidden">{item.viewCount || '0'}</span>
                        </div>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 mr-1" />
                          <span className="hidden lg:inline">{item.likeCount?.toLocaleString() || '0'}</span>
                          <span className="lg:hidden">{item.likeCount || '0'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleViewContent(item)}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded transition-colors"
                          title="View"
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleEditContent(item)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {item.status.toLowerCase() === 'published' && (
                          <button
                            onClick={() => handleNotifyUsers(item)}
                            disabled={notifyingContentId === item.id}
                            className="text-green-600 hover:text-green-900 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Notify Users"
                          >
                            <Bell className={`h-4 w-4 ${notifyingContentId === item.id ? 'animate-pulse' : ''}`} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteContent(item.contentType, item.id, item.title)}
                          className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                disabled={filters.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handleFilterChange('page', Math.min(totalPages, filters.page + 1))}
                disabled={filters.page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((filters.page - 1) * filters.page_size) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page * filters.page_size, totalCount)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                    disabled={filters.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, filters.page - 2)) + i;
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleFilterChange('page', pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          filters.page === pageNum
                            ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handleFilterChange('page', Math.min(totalPages, filters.page + 1))}
                    disabled={filters.page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        danger={confirmDialog.danger}
      />

      {/* Content Form Modal */}
      <FullContentFormModal
        isOpen={contentFormModal.isOpen}
        mode={contentFormModal.mode}
        contentItem={contentFormModal.contentItem}
        onClose={handleCloseContentModal}
        onSave={handleContentSave}
      />
    </div>
  );
};

export default ContentManagement;